import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, Plus, LayoutGrid, ArrowRight } from 'lucide-react';
import MobileView from './MobileView';
import { useExperience } from '../../../stores/Experience/Experience';

const CURRENCIES = [

    { code: 'QAR', symbol: 'QR', name: 'Qatari Riyal' },
    { code: 'DZD', symbol: 'DZD', name: 'Algerian Dinar' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'SAR', symbol: '⃁', name: 'Saudi Riyal' },
    { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
];



const THEME_COLOR = '#c59253';

const SiteConfig = ({ state, updateState }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const terminology = useExperience.getState().getTerminology();
    const handleFieldChange = (field, value) => {
        updateState({
            siteInfo: {
                ...state.siteInfo,
                [field]: value,
            },
        });
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Create a preview URL for the UI
            const previewUrl = URL.createObjectURL(file);

            updateState({
                siteInfo: {
                    ...state.siteInfo,
                    logo: file, // Store the raw File object for submission
                    logoPreview: previewUrl, // Store preview URL for UI
                },
            });
        }
    };

    const handleFieldsChange = (updates) => {
        updateState({
            siteInfo: {
                ...state.siteInfo,
                ...updates,
            },
        });
    };


    const businessTypeLabels = {
        restaurant: 'restaurant',
        office: 'office',
        beach: 'beach resort',
    };

    const placeholder = `${t('e.g., My')} ${businessTypeLabels[state.businessType] || t('Business')}`;

    return (
        <div className="h-100 d-flex flex-column flex-lg-row overflow-hidden">
            {/* Left: Form */}
            <div className="flex-grow-1 p-3 p-lg-5 overflow-auto bg-white">
                <div className="mx-auto" style={{ maxWidth: '650px' }}>
                    <div className="text-center text-lg-start mb-4">
                        <h1 className="h3 mb-2 fw-bold">{t('Setup Your')} {terminology.business}{t("'s Header")}</h1>
                        <p className="text-muted small">{t('Insert your')} {terminology.business} {t('details')}</p>
                    </div>

                    <div className="d-flex flex-column gap-3 gap-lg-4">
                        {/* Logo Upload - TOP for mobile */}
                        <div className="card border-0 bg-light rounded-4 p-3 mb-2">
                            <div className="d-flex align-items-center gap-3">
                                <div className="position-relative">
                                    {state.siteInfo?.logoPreview ? (
                                        <img
                                            src={state.siteInfo.logoPreview}
                                            alt="Logo"
                                            className="rounded-circle border shadow-sm object-fit-cover"
                                            style={{ width: '70px', height: '70px' }}
                                        />
                                    ) : (
                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-circle bg-white border border-2 border-dashed shadow-sm"
                                            style={{ width: '70px', height: '70px' }}
                                        >
                                            <Upload className="text-muted" size={20} />
                                        </div>
                                    )}
                                    <label
                                        className="position-absolute bottom-0 end-0 bg-white shadow-sm rounded-circle d-flex align-items-center justify-content-center cursor-pointer border hover-shadow"
                                        style={{ width: '28px', height: '28px' }}
                                    >
                                        <Plus size={14} style={{ color: THEME_COLOR }} />
                                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="d-none" />
                                    </label>
                                </div>
                                <div>
                                    <h6 className="mb-1 fw-bold">{terminology.business} {t('Logo')}</h6>
                                    <p className="text-muted small mb-0">{t('Visible on your digital menu')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Basic Info Row */}
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{t('Your')} {terminology.business}{t("'s name")}</label>
                                <input
                                    type="text"
                                    value={state.siteInfo?.businessName || ''}
                                    onChange={(e) => handleFieldChange('businessName', e.target.value)}
                                    className="form-control border-2 shadow-none rounded-3"
                                    style={{ padding: '0.6rem 1rem' }}
                                    placeholder={placeholder}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label small fw-bold text-uppercase text-muted d-flex justify-content-between" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                    {t('In Arabic')} ({t('Optional')})
                                    <span className="text-lowercase opacity-50 fw-normal">{t('Optional')}</span>
                                </label>
                                <input
                                    type="text"
                                    dir="rtl"
                                    value={state.siteInfo?.name_ar || ''}
                                    onChange={(e) => handleFieldChange('name_ar', e.target.value)}
                                    className="form-control border-2 shadow-none rounded-3"
                                    style={{ padding: '0.6rem 1rem' }}
                                    placeholder="اسم المطعم..."
                                />
                            </div>
                        </div>

                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{t('Currency')}</label>
                                <select
                                    value={state.siteInfo?.currency || 'QAR'}
                                    onChange={(e) => handleFieldChange('currency', e.target.value)}
                                    className="form-select border-2 shadow-none rounded-3"
                                    style={{ padding: '0.6rem 1rem' }}
                                >
                                    {CURRENCIES.map((currency) => (
                                        <option key={currency.code} value={currency.code}>
                                            {currency.symbol} ({t(currency.code)})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>



                    </div>
                </div>
            </div>

            {/* Right: Live Preview - Hidden on Mobile */}
            <div className="d-none d-lg-flex col-lg-5 bg-light p-4 p-lg-5 border-start" style={{ flexDirection: 'column', overflow: 'hidden' }}>
                <h3 className="text-center text-muted small text-uppercase fw-bold mb-4 flex-shrink-0">{t('Live Preview')}</h3>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                    <div className="border rounded-4 bg-white shadow-sm overflow-hidden" style={{ width: '320px', height: '100%', maxHeight: '640px' }}>
                        <MobileView state={state} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SiteConfig;
